import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from '@/lib/toast';
import GoogleSignInButton from '@/components/GoogleSignInButton';

const AUTH_BG = 'https://images.pexels.com/photos/15455314/pexels-photo-15455314.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

export function AuthFrame({ title, subtitle, children }) {
    return (
        <div className="relative min-h-screen flex items-center justify-center px-6 py-12 overflow-hidden">
            <div
                className="absolute inset-0 opacity-40"
                style={{ backgroundImage: `url(${AUTH_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-obsidian/80 to-obsidian" aria-hidden="true" />
            <Card className="relative z-10 w-full max-w-md glass rounded-sm p-8 md:p-10 border-marigold/15">
                <Link to="/" className="flex items-center gap-2 mb-8" data-testid="auth-logo">
                    <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                        <circle cx="16" cy="16" r="13" stroke="#F59E0B" strokeWidth="1" opacity="0.6" />
                        <circle cx="16" cy="16" r="5" stroke="#FDE68A" strokeWidth="0.8" />
                    </svg>
                    <span className="font-serif text-2xl text-parchment">AstroConsult</span>
                </Link>
                <h1 className="font-serif text-3xl text-parchment">{title}</h1>
                {subtitle && <p className="mt-2 text-sm text-slate-400">{subtitle}</p>}
                <div className="mt-8">{children}</div>
            </Card>
        </div>
    );
}

export default function Login() {
    const { login, loading } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            const u = await login(email, password);
            toast.success(`Welcome back, ${u.name}`);
            navigate(u.birth_details?.date ? '/app' : '/onboarding');
        } catch (err) {
            const detail = err.response?.data?.detail;
            // Round 25 — backend returns structured 403 for unverified accounts.
            if (typeof detail === 'object' && detail?.code === 'EMAIL_NOT_VERIFIED') {
                toast.info('Please verify your email — we sent a fresh code.');
                navigate('/verify', { state: { email: detail.email || email } });
                return;
            }
            toast.error(typeof detail === 'string' ? detail : 'Login failed');
        }
    };

    return (
        <AuthFrame title="Welcome back" subtitle="Sign in to consult the stars.">
            <div className="space-y-5">
                <GoogleSignInButton label="Continue with Google" testId="login-google-btn" />
                <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-500">
                    <div className="flex-1 h-px bg-marigold/15" />
                    <span>or with email</span>
                    <div className="flex-1 h-px bg-marigold/15" />
                </div>
                <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                <div>
                    <Label htmlFor="email" className="text-slate-300 text-xs uppercase tracking-wider">Email</Label>
                    <Input
                        id="email" type="email" required autoFocus
                        value={email} onChange={(e) => setEmail(e.target.value)}
                        className="mt-2 bg-transparent border-b border-marigold/20 border-t-0 border-l-0 border-r-0 rounded-none px-0 focus-visible:ring-0 focus-visible:border-marigold text-parchment h-11"
                        data-testid="login-email"
                    />
                </div>
                <div>
                    <div className="flex items-center justify-between">
                        <Label htmlFor="password" className="text-slate-300 text-xs uppercase tracking-wider">Password</Label>
                        <Link to="/forgot" className="text-xs text-marigold hover:text-marigold-50" data-testid="link-forgot">Forgot?</Link>
                    </div>
                    <Input
                        id="password" type="password" required
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        className="mt-2 bg-transparent border-b border-marigold/20 border-t-0 border-l-0 border-r-0 rounded-none px-0 focus-visible:ring-0 focus-visible:border-marigold text-parchment h-11"
                        data-testid="login-password"
                    />
                </div>
                <Button type="submit" disabled={loading}
                    data-testid="login-submit"
                    className="w-full bg-marigold text-obsidian hover:bg-marigold-hover rounded-none h-11 font-medium">
                    {loading ? 'Signing in…' : 'Sign in'}
                </Button>
                <p className="text-sm text-slate-400 text-center">
                    New here?{' '}
                    <Link to="/signup" className="text-marigold hover:text-marigold-50" data-testid="link-signup">
                        Create an account
                    </Link>
                </p>
            </form>
            </div>
        </AuthFrame>
    );
}
